/** One answer option on the student screen. Text or image; 48px+ target; states default,
 * selected, submitted, correct, incorrect, disabled. Correctness is shown with a glyph and a
 * label, never colour alone. Implemented as a radio-style button so a group is keyboard and
 * screen-reader navigable (arrow keys via the parent `role="radiogroup"`). */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type OptionState = "default" | "selected" | "submitted" | "correct" | "incorrect" | "disabled";

type Props = {
  label: string;
  state?: OptionState;
  image?: { src: string; alt: string };
  /** Multi-select items are checkboxes, not radios. */
  multi?: boolean;
  /** No option text (asked aloud): the letter is the whole card, centred and large. */
  bare?: boolean;
  onSelect?: () => void;
  children?: ReactNode;
};

export function OptionCard({ label, state = "default", image, multi = false, bare = false, onSelect, children }: Props) {
  const { t } = useTranslation();
  const chosen = state === "selected" || state === "submitted" || state === "correct" || state === "incorrect";
  const border =
    state === "correct" ? "var(--secure)" : state === "incorrect" ? "var(--not-yet)" : chosen ? "var(--accent)" : "transparent";
  const off = state === "disabled";
  return (
    <button
      type="button"
      role={multi ? "checkbox" : "radio"}
      aria-checked={chosen}
      aria-disabled={off || undefined}
      disabled={off}
      onClick={off ? undefined : onSelect}
      className={[
        "flex min-h-14 w-full items-center gap-3 rounded-[var(--radius-card)] border-2 bg-[color:var(--surface)] p-3 text-left text-base shadow-sm transition-[border-color,transform] duration-150 hover:border-[color:var(--line)] active:scale-[0.99]",
        off ? "cursor-not-allowed opacity-50" : "",
      ].join(" ")}
      style={{ borderColor: border }}
    >
      <span
        aria-hidden
        className={bare ? "flex h-14 w-14 shrink-0 items-center justify-center rounded-full border text-2xl font-bold" : "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border font-semibold"}
        style={{ borderColor: chosen ? border : "var(--ink-2)", background: chosen ? border : "transparent", color: chosen ? "var(--accent-ink)" : "var(--ink)" }}
      >
        {label}
      </span>
      {image ? (
        <span className="min-w-0 flex-1">
          <img src={image.src} alt={image.alt} className="max-h-40 w-auto max-w-full rounded-sm object-contain" loading="lazy" />
        </span>
      ) : (
        <span className="min-w-0 flex-1 break-words">{children}</span>
      )}
      {state === "correct" && (
        <span className="ml-auto text-sm font-medium" style={{ color: "var(--secure)" }}>
          ✓ {t("option.correct")}
        </span>
      )}
      {state === "incorrect" && (
        <span className="ml-auto text-sm font-medium" style={{ color: "var(--not-yet)" }}>
          ✗ {t("option.incorrect")}
        </span>
      )}
      {state === "submitted" && <span className="ml-auto text-sm text-[color:var(--ink-2)]">{t("option.sent")}</span>}
    </button>
  );
}
